import type { Dispatch, SetStateAction } from 'react';
import type { ActivityState } from '../../types';

import styled from '@emotion/styled';
import { updateActivity } from '@kizen/api/activities';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fontWeights } from 'app/typography';
import Icon from '__components/Kizen/Icon';
import Loader from '__components/Kizen/Loader';
import Switch from '__components/Kizen/Switch';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { useToast } from '__components/ToastProvider';
import MultiColumnBuilder from 'components/MultiColumnBuilder';
import BaseOption from '__components/MultiColumnBuilder/components/BaseOption';
import useBasicBuilder, {
  LEFT_ID,
  RIGHT_ID,
} from 'components/MultiColumnBuilder/useBasicBuilder';
import { allCustomObjectsQuery } from 'query/custom-object';
import AxiosService from 'services/AxiosService';
import { snakeToCamelCaseKeys } from '__services/helpers';

type AssociatedObject = ActivityState['associatedObjects'][number];

type AssociationBuilderProps = {
  activity: ActivityState;
  setActivity: Dispatch<SetStateAction<ActivityState>>;
};

interface BuilderProps extends AssociationBuilderProps {
  customObjects: Array<{ id: string; label: string }>;
}

type OptionProps = {
  [key: string]: any;
  element: Element;
  onDelete(element: Element): void;
  onRequiredChange(value: boolean): void;
  onReadonlyChange(value: boolean): void;
};

type Element = {
  id: string;
  isReadonly: boolean;
  isRequired: boolean;
  label: string;
};

const selfDisabledDropZones = [LEFT_ID];

const MultiColumnBuilderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 330px;
  background-color: var(--color-background-white);
`;

const OptionTogglesContainer = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 20px;
  flex: 1;
  margin-right: 20px;
`;

const SwitchWithTooltip = styled(Switch)`
  min-width: 117px;
`;

const ElementLabel = styled(TextEllipsisWithTooltip)`
  max-width: 250px;
`;

const Option = (props: OptionProps) => {
  const { onDelete, onRequiredChange, onReadonlyChange, ...rest } = props;
  const { element, section } = rest;

  const { t } = useTranslation();
  const [readonly, setReadonly] = useState(element.isReadonly);
  const [required, setRequired] = useState(element.isRequired);

  const readonlyTooltip =
    required && readonly
      ? t(
          "When association is empty, it will still be editable because it's required."
        )
      : null;

  const additionalActions = useCallback(
    ({ swapItemColumn, index, element: elem, section }: any) => {
      const showDelete = section === RIGHT_ID;

      if (!elem || !showDelete || !swapItemColumn) {
        return null;
      }

      return (
        <div>
          <Icon
            className="DeleteAction"
            icon="delete"
            onClick={() => {
              swapItemColumn(index, section);
              onDelete(element);
            }}
          />
        </div>
      );
    },
    [element, onDelete]
  );

  return (
    <BaseOption additionalActions={additionalActions} {...rest}>
      {({ element }: any) => {
        return (
          <>
            <ElementLabel>{element.label}</ElementLabel>
            {section === RIGHT_ID && (
              <OptionTogglesContainer>
                <Switch
                  removeMargin
                  checked={required}
                  label={t('Required')}
                  onChange={() => {
                    onRequiredChange(!required);
                    setRequired(!required);
                  }}
                />
                <SwitchWithTooltip
                  removeMargin
                  checked={readonly}
                  label={t('Readonly')}
                  tooltipPlacement="right"
                  tooltip={readonlyTooltip}
                  onChange={() => {
                    onReadonlyChange(!readonly);
                    setReadonly(!readonly);
                  }}
                />
              </OptionTogglesContainer>
            )}
          </>
        );
      }}
    </BaseOption>
  );
};

const Builder = (props: BuilderProps) => {
  const { activity, customObjects, setActivity } = props;
  const { associatedObjects } = activity;

  const { t } = useTranslation();
  const [showToast] = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [associations] = useState<Map<string, AssociatedObject>>(
    new Map(
      activity.associatedObjects.map((ao) => {
        return [ao.customObject.id, ao];
      })
    )
  );

  const initialItems = useMemo(() => {
    const left: Element[] = [];
    const right: Element[] = [];

    for (const co of customObjects ?? []) {
      const index = associatedObjects.findIndex(
        (ao) => ao.customObject.id === co.id
      );

      if (index >= 0) {
        const item = associatedObjects[index];

        right[index] = {
          ...co,
          isReadonly: item.isReadonly,
          isRequired: item.isRequired,
        };
      } else {
        left.push({
          ...co,
          isReadonly: false,
          isRequired: false,
        });
      }
    }

    return { left, right };
  }, [customObjects, associatedObjects]);

  const builder = useBasicBuilder({
    editable: true,
    initialLeftColumnItems: initialItems.left,
    initialRightColumnItems: initialItems.right,
    showCategoryOnSearch: false,
    selfDisabledDropZones,
    rightColumnItemsWithContentForceDirty: false,
  });

  const {
    setOptionsSearchTerm,
    rightColumnItems,
    leftColumnHeaderProps,
    ...rest
  } = builder;

  const { dirty } = builder;

  const handleActivityUpdate = useCallback(
    async (body: Parameters<typeof updateActivity>['2']) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      const res = await updateActivity(AxiosService, activity.id, body, {
        signal: abortControllerRef.current.signal,
      });

      const camel = snakeToCamelCaseKeys(res);

      setActivity((prev) => {
        return {
          ...prev,
          associatedObjects: camel.associatedObjects,
          customObjects: camel.customObjects,
        };
      });
    },
    [activity.id, setActivity]
  );

  const handleDelete = useCallback(
    (element: Element) => {
      associations.delete(element.id);
    },
    [associations]
  );

  useEffect(() => {
    if (dirty && rightColumnItems.length) {
      handleActivityUpdate({
        association_mode: 'selected_objects_associated',
        associated_objects: rightColumnItems.map((item, index) => {
          const current = associations.get(item.id);
          const is_readonly = current?.isReadonly ?? false;
          const is_required = current?.isRequired ?? false;

          return {
            custom_object: { id: item.id },
            is_readonly,
            is_required,
            order: index,
          };
        }),
      });
    }

    if (dirty && !rightColumnItems.length) {
      showToast({
        variant: 'failure',
        message: t(
          'At least one Object must be selected for the "Select Objects" option in Object Associations to work correctly.'
        ),
      });
    }
  }, [
    dirty,
    associations,
    rightColumnItems,
    handleActivityUpdate,
    showToast,
    t,
  ]);

  return (
    <MultiColumnBuilderWrapper>
      <MultiColumnBuilder
        {...rest}
        weight={fontWeights.regular}
        leftHeaderText={t('Available Objects (Drag to Add)')}
        rightHeaderText={t('Selected Objects')}
        searchPlaceholder={t('Find Options')}
        onChangeSearchTerm={setOptionsSearchTerm}
        disabledDropZoneText={false}
        rollBackDrop={false}
        leftColumnHeaderProps={{
          ...leftColumnHeaderProps,
          zIndex: 'unset',
        }}
        renderRightEmpty={{
          onDropLabel: t('Place Here'),
          noItems: t('No Columns Currently Selected (Drag and Drop to Add)'),
        }}
        renderLeftEmpty={{
          onDropLabel: t('Drop to Remove'),
          noItems: t('No Options Found'),
        }}
        renderOption={(props: any) => {
          const { element } = props;

          return (
            <Option
              {...props}
              onDelete={handleDelete}
              onRequiredChange={(value) => {
                associations.set(element.id, {
                  ...associations.get(element.id)!,
                  isRequired: value,
                });

                handleActivityUpdate({
                  associated_objects: rightColumnItems.map((item, index) => {
                    const current = associations.get(item.id);
                    const { isReadonly = false, isRequired = false } =
                      current ?? {};

                    return {
                      custom_object: { id: item.id },
                      is_readonly: isReadonly,
                      is_required: element.id === item.id ? value : isRequired,
                      order: index,
                    };
                  }),
                });
              }}
              onReadonlyChange={(value) => {
                associations.set(element.id, {
                  ...associations.get(element.id)!,
                  isReadonly: value,
                });

                handleActivityUpdate({
                  associated_objects: rightColumnItems.map((item, index) => {
                    const current = associations.get(item.id);
                    const { isReadonly = false, isRequired = false } =
                      current ?? {};

                    return {
                      custom_object: { id: item.id },
                      is_readonly: element.id === item.id ? value : isReadonly,
                      is_required: isRequired,
                      order: index,
                    };
                  }),
                });
              }}
            />
          );
        }}
      />
    </MultiColumnBuilderWrapper>
  );
};

export const AssociationBuilder = (props: AssociationBuilderProps) => {
  const customObjectsQuery = useQuery(
    allCustomObjectsQuery({
      params: { default_on_activities: true },
      staleTime: Infinity,
      select: (data) => {
        return data.map((obj) => {
          return {
            id: obj.id,
            label: obj.object_name,
          };
        });
      },
    })
  );

  if (customObjectsQuery.isLoading) {
    return (
      <MultiColumnBuilderWrapper>
        <Loader loading />
      </MultiColumnBuilderWrapper>
    );
  }

  return <Builder customObjects={customObjectsQuery.data!} {...props} />;
};
